import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';

const BusinessDebts = () => {
  const outletContext = useOutletContext();
  const canManage = outletContext?.canManageDebts ?? true;
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);

  useEffect(() => {
    loadDebts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadDebts = async () => {
    try {
      let url = '/business/debts';
      if (filter !== 'all') url += `?statut=${filter}`;
      const res = await api.get(url);
      setDebts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dette ?')) return;
    try {
      await api.delete(`/business/debts/${id}`);
      loadDebts();
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      en_cours: 'bg-amber-100 text-amber-600',
      partiellement_payee: 'bg-blue-100 text-blue-600',
      payee: 'bg-green-100 text-green-600'
    };
    const labels = {
      en_cours: 'En cours',
      partiellement_payee: 'Partielle',
      payee: 'Soldée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[statut] || styles.en_cours}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  // Stats
  const stats = {
    totalDue: debts.filter(d => d.statut !== 'payee').reduce((sum, d) => {
      return sum + parseFloat(d.montant_restant || 0);
    }, 0),
    countActive: debts.filter(d => d.statut !== 'payee').length,
    countPaid: debts.filter(d => d.statut === 'payee').length,
    overdue: debts.filter(d => d.statut !== 'payee' && d.date_echeance && new Date(d.date_echeance) < new Date()).length
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredDebts = debts.filter(debt => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      debt.fournisseur?.toLowerCase().includes(searchLower) ||
      debt.description?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredDebts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDebts = filteredDebts.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Dettes Fournisseurs</h1>
          <p className="text-gray-500 text-sm">Gérez vos dettes et échéances</p>
        </div>
        <button
          onClick={() => { setEditingDebt(null); setShowModal(true); }}
          className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition flex items-center justify-center gap-2"
        >
          <span>➕</span> Nouvelle dette
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2">💳</div>
          <div className="text-lg md:text-xl font-bold text-red-600">{stats.totalDue.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">À rembourser</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-amber-500 text-xl md:text-2xl mb-1 md:mb-2">⏳</div>
          <div className="text-xl md:text-2xl font-bold text-amber-600">{stats.countActive}</div>
          <div className="text-xs md:text-sm text-gray-500">Actives</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-green-500 text-xl md:text-2xl mb-1 md:mb-2">✅</div>
          <div className="text-xl md:text-2xl font-bold text-green-600">{stats.countPaid}</div>
          <div className="text-xs md:text-sm text-gray-500">Soldées</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2">🚨</div>
          <div className="text-xl md:text-2xl font-bold text-red-600">{stats.overdue}</div>
          <div className="text-xs md:text-sm text-gray-500">En retard</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        {/* Barre de recherche */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par fournisseur, description..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {[
            { value: 'all', label: '📋 Toutes' },
            { value: 'en_cours', label: '⏳ En cours' },
            { value: 'partiellement_payee', label: '💰 Partielles' },
            { value: 'payee', label: '✅ Soldées' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredDebts.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Debts List */}
      {paginatedDebts.length > 0 ? (
        <div className="space-y-4">
          {paginatedDebts.map(debt => {
            const total = parseFloat(debt.montant_initial) || 0;
            const reste = parseFloat(debt.montant_restant) || 0;
            const paye = total - reste;
            const progress = total > 0 ? (paye / total) * 100 : 0;
            const isOverdue = debt.date_echeance && new Date(debt.date_echeance) < new Date() && debt.statut !== 'payee';
            
            return (
              <div key={debt.id} className={`bg-white rounded-xl shadow p-6 ${isOverdue ? 'border-l-4 border-red-500' : ''}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-800 text-lg">{debt.fournisseur}</h3>
                      {getStatusBadge(debt.statut)}
                      {isOverdue && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                          🚨 En retard
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm mb-2">{debt.description || 'Pas de description'}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span>📅 {new Date(debt.date_dette).toLocaleDateString()}</span>
                      {debt.date_echeance && (
                        <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                          ⏰ Échéance: {new Date(debt.date_echeance).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="w-full md:w-64">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Progression</span>
                      <span className="font-medium text-gray-800">{Math.round(progress)}%</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Payé: {paye.toLocaleString()} XOF</span>
                      <span className="text-red-600 font-bold">Reste: {reste.toLocaleString()} XOF</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {debt.statut !== 'payee' && (
                      <button
                        onClick={() => { setSelectedDebt(debt); setShowPaymentModal(true); }}
                        className="px-4 py-2 bg-green-100 text-green-600 rounded-xl hover:bg-green-200 transition"
                        title="Effectuer un remboursement"
                      >
                        💰 Rembourser
                      </button>
                    )}
                    <button
                      onClick={() => { setEditingDebt(debt); setShowModal(true); }}
                      className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(debt.id)}
                      className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                {/* Payments history */}
                {debt.payments && debt.payments.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Historique des remboursements</h4>
                    <div className="flex flex-wrap gap-2">
                      {debt.payments.map((p, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                          {new Date(p.date_paiement).toLocaleDateString()} - {parseFloat(p.montant).toLocaleString()} XOF
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredDebts.length)} sur {filteredDebts.length}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                    showPages.add(i);
                  }
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((page, idx) => {
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                      pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  });
                  return pages;
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">💳</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucune dette'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Essayez une autre recherche' : 'Vous n\'avez aucune dette enregistrée'}
          </p>
          {!search && (
            <button
              onClick={() => { setEditingDebt(null); setShowModal(true); }}
              className="px-6 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition"
            >
              Ajouter une dette
            </button>
          )}
        </div>
      )}

      {/* Debt Modal */}
      {showModal && (
        <DebtModal
          debt={editingDebt}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadDebts();
          }}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedDebt && (
        <DebtPaymentModal
          debt={selectedDebt}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            loadDebts();
          }}
        />
      )}
    </div>
  );
};

// Debt Modal Component
const DebtModal = ({ debt, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    fournisseur: debt?.fournisseur || '',
    description: debt?.description || '',
    montant_initial: debt?.montant_initial || debt?.montant_restant || '',
    date_dette: debt?.date_dette?.split(' ')[0] || new Date().toISOString().split('T')[0],
    date_echeance: debt?.date_echeance?.split(' ')[0] || '',
    notes: debt?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (debt) {
        await api.put(`/business/debts/${debt.id}`, form);
      } else {
        await api.post('/business/debts', form);
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            {debt ? 'Modifier la dette' : 'Nouvelle dette'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Créancier/Fournisseur *</label>
            <input
              type="text"
              value={form.fournisseur}
              onChange={(e) => setForm({ ...form, fournisseur: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Nom du fournisseur"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Objet de la dette"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant total *</label>
            <input
              type="number"
              min="0"
              value={form.montant_initial}
              onChange={(e) => setForm({ ...form, montant_initial: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de la dette</label>
              <input
                type="date"
                value={form.date_dette}
                onChange={(e) => setForm({ ...form, date_dette: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
              <input
                type="date"
                value={form.date_echeance}
                onChange={(e) => setForm({ ...form, date_echeance: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : debt ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Debt Payment Modal Component
const DebtPaymentModal = ({ debt, onClose, onSuccess }) => {
  const reste = parseFloat(debt.montant_restant) || 0;
  const [form, setForm] = useState({
    montant: reste,
    mode_paiement: 'especes',
    reference: '',
    date_paiement: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/business/debts/${debt.id}/payments`, form);
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Remboursement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-xl">
          <div className="font-medium text-gray-800">{debt.fournisseur}</div>
          <div className="text-lg font-bold text-red-600">Reste à payer: {reste.toLocaleString()} XOF</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant du remboursement *</label>
            <input
              type="number"
              min="0"
              max={reste}
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select
              value={form.mode_paiement}
              onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="especes">💵 Espèces</option>
              <option value="mobile_money">📱 Mobile Money</option>
              <option value="virement">🏦 Virement</option>
              <option value="cheque">📝 Chèque</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={form.date_paiement}
              onChange={(e) => setForm({ ...form, date_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : '💰 Confirmer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessDebts;
